import cronstrue from "cronstrue/i18n";
import CronParser, {
  DayOfTheMonthRange,
  DayOfTheWeekRange,
  HourRange,
  MonthRange,
  SixtyRange,
} from "cron-parser";
import i18n from "i18next";
import { LocaleUtil } from "@utils/locale.util";

type Frequency = "OneTime" | "Daily" | "Weekly";

export class CronUtil {
  static toHumanReadable(cronExpression: string): string {
    return cronstrue.toString(cronExpression, {
      verbose: false,
      locale:
        i18n.language &&
        LocaleUtil.getRootLanguageCodeFromLocaleString(i18n.language),
    });
  }

  static getNextDate(cronExpression: string, startDate?: Date): Date {
    return CronParser.parseExpression(cronExpression, {
      currentDate: startDate ?? new Date(),
    })
      .next()
      .toDate();
  }

  static generateCron({
    dayOfWeek,
    frequency,
    time,
    date,
  }: {
    dayOfWeek: number[];
    frequency: Frequency;
    time: Date;
    date: Date;
  }): string {
    const hour = time.getHours();
    const minute = time.getMinutes();

    const getRange = <T>(num: number, start = 1): T[] => {
      return Array.from(Array(num)).map((_, index) => (index + start) as T);
    };

    return CronParser.fieldsToExpression(
      {
        second: [0],
        minute: [minute] as SixtyRange[],
        hour: [hour] as HourRange[],
        dayOfWeek:
          frequency === "Weekly"
            ? (dayOfWeek as DayOfTheWeekRange[])
            : getRange<DayOfTheWeekRange>(7, 0),
        dayOfMonth:
          frequency === "OneTime"
            ? ([date.getDate()] as DayOfTheMonthRange[])
            : getRange<DayOfTheMonthRange>(31),
        month:
          frequency === "OneTime"
            ? ([date.getMonth() + 1] as MonthRange[])
            : getRange<MonthRange>(12),
      },
      { iterator: false },
    ).stringify();
  }

  static parseCron(cron: string): {
    dayOfWeek: number[];
    frequency: Frequency;
    time: Date;
  } {
    const interval = CronParser.parseExpression(cron);
    const { hour, minute, dayOfWeek, dayOfMonth, month } = JSON.parse(
      JSON.stringify(interval.fields),
    );
    const date = new Date();
    date.setHours(hour[0]);
    date.setMinutes(minute[0]);
    if (dayOfMonth.length === 1) {
      date.setDate(dayOfMonth[0]);
    }
    if (month.length === 1) {
      date.setMonth(month[0] - 1);
    }

    let frequency: Frequency = "Daily";

    if (dayOfWeek.length > 0 && dayOfWeek.length < 7) {
      frequency = "Weekly";
    }
    if (month.length === 1 && dayOfMonth.length === 1) {
      frequency = "OneTime";
    }

    return {
      dayOfWeek,
      frequency,
      time: date,
    };
  }

  static getNextTimestamps(
    cronExpression: string,
    maxTimestamp: number,
  ): number[] {
    const timestamps: number[] = [];
    const cron = CronParser.parseExpression(cronExpression, {
      currentDate: new Date(),
    });

    while (cron.hasNext()) {
      const next = cron.next();
      if (next.getTime() > maxTimestamp) {
        break;
      }
      timestamps.push(next.getTime());
    }

    return timestamps;
  }
}
